import { useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import { useDialogKeyboard } from "@/lib/useDialogKeyboard";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  pendingLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  pending?: boolean;
  confirmDisabled?: boolean;
  variant?: "destructive" | "primary";
  overlayClassName?: string;
  // Optional middle action between Cancel and the primary confirm — e.g. the
  // media gallery's "Remove This Copy" next to "Delete Everywhere". Rendered
  // only when both secondaryLabel and onSecondary are provided.
  secondaryLabel?: string;
  secondaryPendingLabel?: string;
  onSecondary?: () => void;
  // Which in-flight action the pendingLabel belongs to: while `pending` is
  // true both buttons disable, but only the one that was clicked shows its
  // pending text.
  secondaryPending?: boolean;
  // Optional extra content rendered between the message and the buttons —
  // e.g. the text-blast recipient scope picker. Keep it lightweight; this is
  // still a confirm dialog, not a form.
  children?: React.ReactNode;
}

const FONT: React.CSSProperties = { fontFamily: "Inter, sans-serif" };

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Delete",
  pendingLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  pending,
  confirmDisabled,
  variant = "destructive",
  overlayClassName = "z-50",
  secondaryLabel,
  secondaryPendingLabel,
  onSecondary,
  secondaryPending,
  children,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const handleKeyDown = useDialogKeyboard({
    open,
    onClose: onCancel,
    dialogRef,
    initialFocus: '[data-testid="button-cancel-confirm"]',
  });

  if (!open) return null;

  const isDestructive = variant === "destructive";
  const iconWrapClass = isDestructive ? "bg-rose-700/10" : "bg-foreground/10";
  const iconClass = isDestructive ? "text-rose-700" : "text-foreground";
  const eyebrowClass = isDestructive ? "text-rose-700" : "text-foreground";
  const eyebrowText = isDestructive ? "Confirm · Destructive" : "Confirm action";
  const confirmBtnClass = isDestructive
    ? "bg-rose-700 hover:bg-rose-800 text-white"
    : "bg-foreground hover:bg-slate-800 text-background";

  return createPortal(
    <div
      className={`fixed inset-0 bg-foreground/40 sm:backdrop-blur-sm flex items-center justify-center ${overlayClassName} p-4`}
      style={FONT}
      onClick={onCancel}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="bg-card border border-border p-4 sm:p-6 rounded-md max-h-[calc(100dvh-2rem)] max-w-md w-full overflow-y-auto shadow-xl space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-md ${iconWrapClass}`}>
            <AlertTriangle size={18} strokeWidth={1.75} className={`${iconClass} shrink-0`} />
          </div>
          <div className="space-y-0.5">
            <span className={`text-[length:var(--text-tiny)] uppercase tracking-[0.16em] font-semibold ${eyebrowClass}`}>
              {eyebrowText}
            </span>
            <h3 id="confirm-dialog-title" className="text-[17px] font-semibold tracking-tight text-foreground">{title}</h3>
          </div>
        </div>
        <p className="text-[14px] text-foreground/70 leading-relaxed whitespace-pre-wrap">{message}</p>
        {children}
        <div className="flex flex-wrap gap-2 justify-end pt-2">
          <button
            data-testid="button-cancel-confirm"
            onClick={onCancel}
            disabled={pending}
            className="ds-btn-secondary disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          {secondaryLabel && onSecondary && (
            <button
              data-testid="button-secondary-action"
              onClick={onSecondary}
              disabled={pending}
              className="ds-btn-secondary disabled:opacity-50"
            >
              {pending && secondaryPending
                ? (secondaryPendingLabel || `${secondaryLabel}…`)
                : secondaryLabel}
            </button>
          )}
          <button
            data-testid="button-confirm-action"
            onClick={onConfirm}
            disabled={pending || confirmDisabled}
            className={`${confirmBtnClass} inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[length:var(--text-small)] font-semibold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {pending && !secondaryPending ? (pendingLabel || `${confirmLabel}…`) : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
